import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  LinearProgress,
} from '@mui/material'
import { AdminDialog } from '@/components/AdminDialog'
import { StatusChip } from 'components/StatusChip'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import { Check as CheckIcon, Close as CloseIcon, Videocam as VideocamIcon } from '@mui/icons-material'
import { getDownloadURL, getStorage, ref } from '@firebase/storage'
import { InspectionChecklistQuestion } from '@otw/models'
import {
  RepairTruck,
  RepairTruckChecklistPhoto,
  RepairTruckChecklistWalkaroundVideo,
  RepairTruckInspectionChecklist,
} from '@otw/models/logistics'
import {
  DEFAULT_CHECKLIST_DOC_ID,
  DEFAULT_INTAKE_CHECKLIST_DOC_ID,
  useInspectionChecklist,
  type InspectionChecklistKind,
} from '@/hooks/repair/useInspectionChecklist'
import MediaGalleryDialog, { type MediaGalleryItem } from './MediaGalleryDialog'
import { checklistAnswerTone } from '../../utils/mobileStatusTones'

export type ChecklistAnswersTab = InspectionChecklistKind

interface InspectionChecklistAnswersDialogProps {
  open: boolean
  onClose: () => void
  repairTruck: RepairTruck
  /** Firestore `inspection_checklist` doc for the complete template. */
  completeChecklistId?: string
  /** Firestore `inspection_checklist` doc for the intake template. */
  intakeChecklistId?: string
  /** Which tab to show when the dialog opens. */
  initialTab?: ChecklistAnswersTab
}

function answerLabel(answer?: string) {
  if (answer === 'yes') return 'Yes'
  if (answer === 'no') return 'No'
  if (answer === 'na') return 'N/A'
  return 'Not answered'
}

function answerAccentColor(answer?: string) {
  if (answer === 'yes') return 'success.main'
  if (answer === 'no') return 'error.main'
  if (answer === 'na') return 'grey.500'
  return 'warning.main'
}

function buildChecklistPhotoGalleryItems(params: {
  photos: RepairTruckChecklistPhoto[]
  questionLabel: string
  startStoragePath?: string
}): MediaGalleryItem[] {
  const items = params.photos
    .filter((photo) => Boolean(photo.storage_path?.trim()))
    .map((photo, index) => ({
      key: photo.storage_path,
      storagePath: photo.storage_path,
      mediaKind: 'photo' as const,
      noteText: params.questionLabel,
      metaText: `Checklist photo ${(index + 1).toLocaleString('en-US')} of ${params.photos.length.toLocaleString('en-US')}`,
    }))

  if (!params.startStoragePath || items.length <= 1) return items

  const startIndex = items.findIndex((item) => item.storagePath === params.startStoragePath)
  if (startIndex <= 0) return items
  return [...items.slice(startIndex), ...items.slice(0, startIndex)]
}

function ChecklistPhotoThumb({
  photo,
  onOpen,
}: {
  photo: RepairTruckChecklistPhoto
  onOpen: () => void
}) {
  const [url, setUrl] = useState<string | null>(null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')

  useEffect(() => {
    let cancelled = false
    setStatus('loading')
    getDownloadURL(ref(getStorage(), photo.storage_path))
      .then((downloadUrl) => {
        if (!cancelled) {
          setUrl(downloadUrl)
          setStatus('ready')
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUrl(null)
          setStatus('error')
        }
      })
    return () => {
      cancelled = true
    }
  }, [photo.storage_path])

  return (
    <Box
      sx={{
        width: 88,
        height: 88,
        borderRadius: 1,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'action.hover',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {status === 'loading' ? (
        <CircularProgress size={18} />
      ) : status === 'ready' && url ? (
        <Box
          component="img"
          src={url}
          alt="Checklist photo"
          onClick={onOpen}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            cursor: 'pointer',
            display: 'block',
          }}
        />
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, textAlign: 'center' }}>
          Unavailable
        </Typography>
      )}
    </Box>
  )
}

function WalkaroundVideoPanel({
  questionNumber,
  walkaround,
  onOpen,
}: {
  questionNumber: number
  walkaround?: RepairTruckChecklistWalkaroundVideo | null
  onOpen: (item: MediaGalleryItem) => void
}) {
  const storagePath = walkaround?.storage_path?.trim() || ''
  const [url, setUrl] = useState<string | null>(null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'error' | 'empty'>('empty')
  const answer = storagePath ? 'yes' : undefined

  useEffect(() => {
    if (!storagePath) {
      setUrl(null)
      setStatus('empty')
      return
    }
    let cancelled = false
    setStatus('loading')
    getDownloadURL(ref(getStorage(), storagePath))
      .then((downloadUrl) => {
        if (!cancelled) {
          setUrl(downloadUrl)
          setStatus('ready')
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUrl(null)
          setStatus('error')
        }
      })
    return () => {
      cancelled = true
    }
  }, [storagePath])

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderLeft: 4,
        borderLeftColor: answerAccentColor(answer),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1,
        }}
      >
        <Typography variant="subtitle2">
          {questionNumber}. Walkaround video
        </Typography>
        <StatusChip
          dense
          tone={checklistAnswerTone(answer)}
          label={answerLabel(answer)}
          icon={answer === 'yes' ? <CheckIcon /> : undefined}
        />
      </Box>
      {status === 'loading' ? (
        <CircularProgress size={18} />
      ) : status === 'ready' && url ? (
        <Box sx={{ mt: 0.25 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
            Video
          </Typography>
          <Box
            onClick={() =>
              onOpen({
                key: storagePath,
                storagePath,
                mediaKind: 'video',
                noteText: `${questionNumber}. Walkaround video`,
                metaText: 'Checklist video',
              })
            }
            sx={{
              width: 88,
              height: 88,
              borderRadius: 1,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.900',
              color: 'common.white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <VideocamIcon fontSize="small" />
          </Box>
        </Box>
      ) : null}
    </Paper>
  )
}

function summarizeAnswers(
  questions: InspectionChecklistQuestion[],
  checklist?: RepairTruckInspectionChecklist | null,
) {
  const responses = checklist?.responses ?? {}
  const enabledQuestions = [...questions]
    .filter((question) => question.enabled !== false)
    .sort((a, b) => a.order - b.order)
  const summary = enabledQuestions.reduce(
    (acc, question) => {
      const answer = responses[question.id]?.answer
      if (answer === 'yes') acc.yes += 1
      else if (answer === 'no') acc.no += 1
      else if (answer === 'na') acc.na += 1
      else acc.unanswered += 1
      return acc
    },
    { yes: 0, no: 0, na: 0, unanswered: 0 },
  )
  const answeredCount = summary.yes + summary.no + summary.na
  const totalCount = enabledQuestions.length
  const hasWalkaroundVideo = Boolean(checklist?.walkaround_video?.storage_path?.trim())
  const progressPercent =
    totalCount > 0
      ? Math.round(((answeredCount + (hasWalkaroundVideo ? 1 : 0)) / (totalCount + 1)) * 100)
      : hasWalkaroundVideo
        ? 100
        : 0
  const displayAnsweredCount = answeredCount + (hasWalkaroundVideo ? 1 : 0)
  const displayTotalCount = totalCount + 1
  return {
    enabledQuestions,
    responses,
    summary,
    answeredCount: displayAnsweredCount,
    totalCount: displayTotalCount,
    hasWalkaroundVideo,
    progressPercent,
  }
}

const ChecklistAnswersPanel: React.FC<{
  kindLabel: string
  checklistName?: string
  questions: InspectionChecklistQuestion[]
  checklist?: RepairTruckInspectionChecklist | null
  isLoading?: boolean
  error?: string | null
  onOpenGallery: (photos: MediaGalleryItem[], videos: MediaGalleryItem[]) => void
}> = ({ kindLabel, checklistName, questions, checklist, isLoading, error, onOpenGallery }) => {
  const { enabledQuestions, responses, answeredCount, totalCount, progressPercent } = summarizeAnswers(
    questions,
    checklist,
  )

  if (isLoading) {
    return (
      <Typography variant="body2" color="text.secondary">
        Loading {kindLabel.toLowerCase()} checklist questions...
      </Typography>
    )
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>
  }

  return (
    <Stack spacing={1.5}>
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box>
            <Typography variant="subtitle2">{kindLabel} progress</Typography>
            {checklistName ? (
              <Typography variant="caption" color="text.secondary">
                {checklistName}
              </Typography>
            ) : null}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {answeredCount}/{totalCount} answered ({progressPercent}%)
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          color={progressPercent === 100 ? 'success' : 'primary'}
          sx={{ height: 8, borderRadius: 999 }}
        />
      </Paper>

      {enabledQuestions.length === 0 ? (
        <Alert severity="info">
          No {kindLabel.toLowerCase()} checklist questions are configured
          {checklistName ? ` for “${checklistName}”` : ''}.
        </Alert>
      ) : null}

      {enabledQuestions.map((question, index) => {
        const response = responses[question.id]
        const answerText = answerLabel(response?.answer)
        const photos = response?.photos ?? []
        const questionLabel = `${index + 1}. ${question.question}`

        return (
          <Paper
            key={question.id}
            variant="outlined"
            sx={{
              p: 1.5,
              borderLeft: 4,
              borderLeftColor: answerAccentColor(response?.answer),
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                mb: 1,
              }}
            >
              <Typography variant="subtitle2">{questionLabel}</Typography>
              <StatusChip
                dense
                tone={checklistAnswerTone(response?.answer)}
                label={answerText}
                icon={
                  response?.answer === 'yes' ? (
                    <CheckIcon />
                  ) : response?.answer === 'no' ? (
                    <CloseIcon />
                  ) : undefined
                }
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              Notes: {response?.notes?.trim() ? response.notes : 'None'}
            </Typography>
            {photos.length > 0 ? (
              <Box sx={{ mt: 1.25 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                  Photos ({photos.length.toLocaleString('en-US')})
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {photos.map((photo) => (
                    <ChecklistPhotoThumb
                      key={photo.storage_path}
                      photo={photo}
                      onOpen={() =>
                        onOpenGallery(
                          buildChecklistPhotoGalleryItems({
                            photos,
                            questionLabel,
                            startStoragePath: photo.storage_path,
                          }),
                          [],
                        )
                      }
                    />
                  ))}
                </Stack>
              </Box>
            ) : null}
          </Paper>
        )
      })}

      <WalkaroundVideoPanel
        questionNumber={enabledQuestions.length + 1}
        walkaround={checklist?.walkaround_video}
        onOpen={(item) => onOpenGallery([], [item])}
      />
    </Stack>
  )
}

/** Resolve complete checklist template id for a repair shop (matches GroupsTab). */
export function resolveCompleteChecklistId(shop?: {
  id?: string
  inspection_checklist_id?: string | null
} | null): string {
  const customId = shop?.inspection_checklist_id?.trim()
  if (customId) return customId
  if (shop?.id) return `repair-shop-${shop.id}`
  return DEFAULT_CHECKLIST_DOC_ID
}

/** Resolve intake checklist template id for a repair shop (matches GroupsTab). */
export function resolveIntakeChecklistId(shop?: {
  id?: string
  intake_checklist_id?: string | null
} | null): string {
  const customId = shop?.intake_checklist_id?.trim()
  if (customId) return customId
  if (shop?.id) return `repair-shop-${shop.id}-intake`
  return DEFAULT_INTAKE_CHECKLIST_DOC_ID
}

const InspectionChecklistAnswersDialog: React.FC<InspectionChecklistAnswersDialogProps> = ({
  open,
  onClose,
  repairTruck,
  completeChecklistId = DEFAULT_CHECKLIST_DOC_ID,
  intakeChecklistId = DEFAULT_INTAKE_CHECKLIST_DOC_ID,
  initialTab = 'complete',
}) => {
  const [activeTab, setActiveTab] = useState<ChecklistAnswersTab>(initialTab)
  const [galleryPhotos, setGalleryPhotos] = useState<MediaGalleryItem[]>([])
  const [galleryVideos, setGalleryVideos] = useState<MediaGalleryItem[]>([])
  const emptyMedia = useMemo(() => [] as MediaGalleryItem[], [])
  const isMobile = useIsMobile()

  const {
    checklist: completeChecklistMeta,
    questions: completeQuestions,
    isLoading: isLoadingComplete,
    error: completeError,
  } = useInspectionChecklist({
    checklistId: completeChecklistId,
    kind: 'complete',
    fallbackToDefault: true,
  })

  const {
    checklist: intakeChecklistMeta,
    questions: intakeQuestions,
    isLoading: isLoadingIntake,
    error: intakeError,
  } = useInspectionChecklist({
    checklistId: intakeChecklistId,
    kind: 'intake',
    fallbackToDefault: true,
  })

  useEffect(() => {
    if (!open) {
      setGalleryPhotos([])
      setGalleryVideos([])
      return
    }
    setActiveTab(initialTab)
  }, [open, initialTab])

  const intakeSummary = summarizeAnswers(intakeQuestions, repairTruck.intake_checklist)
  const completeSummary = summarizeAnswers(completeQuestions, repairTruck.inspection_checklist)

  return (
    <>
      <AdminDialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={isMobile}>
        <DialogTitle>
          Checklist answers
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Intake and complete answers for this repair visit
          </Typography>
        </DialogTitle>
        <DialogContent
          dividers
          sx={isMobile ? { flex: 1, minHeight: 0, overflow: 'auto', p: 0 } : { p: 0 }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, value: ChecklistAnswersTab) => setActiveTab(value)}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}
          >
            <Tab
              value="intake"
              label={`Intake (${intakeSummary.answeredCount}/${intakeSummary.totalCount})`}
            />
            <Tab
              value="complete"
              label={`Complete (${completeSummary.answeredCount}/${completeSummary.totalCount})`}
            />
          </Tabs>
          <Box sx={{ p: 2 }}>
            {activeTab === 'intake' ? (
              <ChecklistAnswersPanel
                kindLabel="Intake"
                checklistName={intakeChecklistMeta?.name}
                questions={intakeQuestions}
                checklist={repairTruck.intake_checklist}
                isLoading={isLoadingIntake}
                error={intakeError}
                onOpenGallery={(photos, videos) => {
                  setGalleryPhotos(photos)
                  setGalleryVideos(videos)
                }}
              />
            ) : (
              <ChecklistAnswersPanel
                kindLabel="Complete"
                checklistName={completeChecklistMeta?.name}
                questions={completeQuestions}
                checklist={repairTruck.inspection_checklist}
                isLoading={isLoadingComplete}
                error={completeError}
                onOpenGallery={(photos, videos) => {
                  setGalleryPhotos(photos)
                  setGalleryVideos(videos)
                }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </AdminDialog>

      <MediaGalleryDialog
        open={galleryPhotos.length > 0 || galleryVideos.length > 0}
        onClose={() => {
          setGalleryPhotos([])
          setGalleryVideos([])
        }}
        photos={galleryPhotos.length > 0 ? galleryPhotos : emptyMedia}
        videos={galleryVideos.length > 0 ? galleryVideos : emptyMedia}
      />
    </>
  )
}

export default InspectionChecklistAnswersDialog
